import { useQuery } from '@apollo/client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Button } from '@ovComponents/2-component/button/button';
import { Card } from '@ovComponents/2-component/card/card';
import { Checkbox } from '@ovComponents/2-component/checkbox/checkbox';
import { Chip } from '@ovComponents/2-component/chip/chip';
import { EmptyState } from '@ovComponents/2-component/emptyState/emptyState';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import {
  Table,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { TextField } from '@ovComponents/2-component/textField/textField';
import {
  ButtonVariants,
  ConfirmationModal,
  ConfirmButtonColors,
} from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import type { ColumnDef } from '@tanstack/react-table';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTradingMutation } from '../hooks/useTradingMutation';
import { SortIndicator, useTradingTable } from '../hooks/useTradingTable';
import {
  FETCH_RECENT_TRADE_TRANSACTIONS,
  FETCH_SUB_TRADE_REQUEST_STATS,
  FETCH_SUB_TRADE_REQUESTS_BLOCKED_CHECK,
  FETCH_SUB_TRADE_REQUESTS_FULL,
} from '../queries';
import {
  ALIGNED_TABLE_SX,
  BOLD_SX,
  BORDER_LEFT,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  SORTABLE_HEADER_SX,
} from '../styles';
import { useTradingHub } from '../tradingHubContext';
import type { TradeTransaction } from '../types';
import {
  calculateQuantity,
  DENSITY_CONFIG,
  exportTradesToCsv,
  isBuyType,
  isSellType,
  tradeSideColor,
} from '../utils';
import {
  CANCEL_SUB_TRADE_REQUESTS_BY_ID,
  GENERATE_BULK_TRADES,
  UPDATE_SUB_TRADE_REQUESTS,
  VERIFY_SUB_TRADE_REQUESTS,
} from '../utils/mutations';
import { StatPill } from './shared';
import TradeRequestRow from './tradeRequestRow';

const TradePipelineSection = () => {
  const { sys } = useThemeTokens();
  const { activeOrganization } = useContext(UserContext);
  const {
    density,
    refreshKey,
    triggerRefresh,
    setCurrentTab,
    setActiveFlowStep,
    setPendingBulkTradeRunId,
    recentlyCreatedSTRIds,
    lastBulkCreationTime,
    tradeStatusPreFilter,
    setTradeStatusPreFilter,
  } = useTradingHub();
  const densityConfig = DENSITY_CONFIG[density];

  const [stateFilter, setStateFilter] = useState<string>('ALL');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [tradeCurrencyFilter, setTradeCurrencyFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showConfirmBatch, setShowConfirmBatch] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const { showToast } = useGlobalToast();
  const [verifySubTradeRequests, { loading: verifying }] = useTradingMutation(
    VERIFY_SUB_TRADE_REQUESTS,
  );
  const [generateBulkTrades, { loading: initiating }] =
    useTradingMutation(GENERATE_BULK_TRADES);
  const [cancelSubTradeRequestsById, { loading: cancelling }] =
    useTradingMutation(CANCEL_SUB_TRADE_REQUESTS_BY_ID);
  const [updateSubTradeRequests] = useTradingMutation(
    UPDATE_SUB_TRADE_REQUESTS,
  );

  const { data: stagedData, refetch: refetchStaged } = useQuery(
    FETCH_SUB_TRADE_REQUEST_STATS,
    {
      variables: {
        input: {
          filter: {
            organizationId: activeOrganization?.id,
            states: ['INITIATED'],
          },
        },
      },
      skip: !activeOrganization?.id,
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: approvedData, refetch: refetchApproved } = useQuery(
    FETCH_SUB_TRADE_REQUEST_STATS,
    {
      variables: {
        input: {
          filter: { organizationId: activeOrganization?.id, states: ['READY'] },
        },
      },
      skip: !activeOrganization?.id,
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: sentData, refetch: refetchSent } = useQuery(
    FETCH_SUB_TRADE_REQUEST_STATS,
    {
      variables: {
        input: {
          filter: {
            organizationId: activeOrganization?.id,
            states: ['REQUESTED'],
          },
        },
      },
      skip: !activeOrganization?.id,
      fetchPolicy: 'cache-and-network',
    },
  );

  const staged = stagedData?.fetchSubTradeRequests?.totalCount || 0;
  const approved = approvedData?.fetchSubTradeRequests?.totalCount || 0;
  const sent = sentData?.fetchSubTradeRequests?.totalCount || 0;

  // Stable blocked-trade detection: always fetches INITIATED+READY regardless of active filter
  const { data: blockedCheckData, refetch: refetchBlockedCheck } = useQuery(
    FETCH_SUB_TRADE_REQUESTS_BLOCKED_CHECK,
    {
      variables: {
        input: {
          filter: {
            organizationId: activeOrganization?.id,
            states: ['INITIATED', 'READY'],
          },
          pagination: { perPage: 500 },
        },
      },
      skip: !activeOrganization?.id,
      fetchPolicy: 'cache-and-network',
    },
  );

  const isTradeBlocked = (t: any): boolean => {
    if (t.state !== 'INITIATED' && t.state !== 'READY') return false;
    const scheduleBlocked =
      t.schedule?.nextDate && new Date(t.schedule.nextDate) > new Date();
    return !!(
      t.blockedUntilAccountActive ||
      t.blockedUntilSubAccountActive ||
      t.blockedUntil ||
      scheduleBlocked
    );
  };

  const blockedStats = useMemo(() => {
    const all = blockedCheckData?.fetchSubTradeRequests?.subTradeRequests || [];
    const blocked = all.filter(isTradeBlocked);
    const blockedIds = new Set(blocked.map((t: any) => t.id));
    return {
      total: blocked.length,
      initiated: blocked.filter((t: any) => t.state === 'INITIATED').length,
      ready: blocked.filter((t: any) => t.state === 'READY').length,
      ids: blockedIds,
    };
  }, [blockedCheckData]);
  const blockedCount = blockedStats.total;

  const {
    loading: loadingMain,
    data: mainData,
    refetch,
  } = useQuery(FETCH_SUB_TRADE_REQUESTS_FULL, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id,
          states:
            stateFilter !== 'ALL' && stateFilter !== 'BLOCKED'
              ? [stateFilter]
              : ['INITIATED', 'READY', 'REQUESTED'],
          type: typeFilter !== 'ALL' ? typeFilter : undefined,
        },
        pagination: {
          sortField: 'settlementDate',
          sortDesc: true,
          perPage: pageSize,
          offSet: (page - 1) * pageSize,
        },
      },
    },
    skip: !activeOrganization?.id || stateFilter === 'BLOCKED',
    fetchPolicy: 'cache-and-network',
  });

  const loading = stateFilter === 'BLOCKED' ? !blockedCheckData : loadingMain;
  const data = stateFilter === 'BLOCKED' ? blockedCheckData : mainData;

  const refetchAll = () => {
    refetch();
    refetchStaged();
    refetchApproved();
    refetchSent();
    refetchBlockedCheck();
  };

  useEffect(() => {
    if (refreshKey > 0) refetchAll();
  }, [refreshKey]);
  useEffect(() => {
    if (tradeStatusPreFilter) {
      if (tradeStatusPreFilter === 'IN_FLIGHT') setStateFilter('ALL');
      setPage(1);
      setTradeStatusPreFilter(null);
    }
  }, [tradeStatusPreFilter, setTradeStatusPreFilter]);

  const trades = data?.fetchSubTradeRequests?.subTradeRequests || [];

  const pendingDisplay = Math.max(0, staged - blockedStats.initiated);
  const readyDisplay = Math.max(0, approved - blockedStats.ready);
  const pipelineTotal = pendingDisplay + readyDisplay + sent + blockedCount;

  const rawServerTotal = data?.fetchSubTradeRequests?.totalCount || 0;
  const paginationTotal =
    stateFilter === 'BLOCKED' ? blockedCount : rawServerTotal;

  const tradeCurrencyOptions = useMemo(() => {
    const ccys = new Set<string>();
    trades.forEach((t: any) => {
      if (t.currency) ccys.add(t.currency);
    });
    return Array.from(ccys).sort();
  }, [trades]);

  const filteredTrades = useMemo(() => {
    let result = trades;

    if (stateFilter === 'BLOCKED') {
      result = result.filter((t: any) => blockedStats.ids.has(t.id));
    } else if (stateFilter !== 'REQUESTED') {
      result = result.filter((t: any) => !blockedStats.ids.has(t.id));
    }

    if (tradeCurrencyFilter !== 'ALL') {
      result = result.filter((t: any) => t.currency === tradeCurrencyFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (t: any) =>
          t.financialProduct?.ticker?.toLowerCase().includes(term) ||
          t.financialProduct?.translatedName?.en
            ?.toLowerCase()
            .includes(term) ||
          t.subAccount?.account?.user?.firstName?.toLowerCase().includes(term),
      );
    }
    return result;
  }, [trades, stateFilter, blockedStats.ids, searchTerm, tradeCurrencyFilter]);

  const activeValue = useMemo(
    () =>
      filteredTrades.reduce((sum: number, t: any) => {
        if (['INITIATED', 'READY', 'REQUESTED'].includes(t.state))
          return sum + Math.abs(t.moneyAllocatedCents || 0);
        return sum;
      }, 0),
    [filteredTrades],
  );

  const toggleSelect = (id: string) => {
    const trade = filteredTrades.find((t: any) => t.id === id);
    if (!trade) return;
    if (trade.state !== 'INITIATED' && trade.state !== 'READY') return;
    if (trade.bulkTradeRun) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const actionableTrades = useMemo(
    () =>
      filteredTrades.filter(
        (t: any) =>
          (t.state === 'INITIATED' || t.state === 'READY') && !t.bulkTradeRun,
      ),
    [filteredTrades],
  );
  const selectAll = () => {
    if (selectedIds.size === actionableTrades.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(actionableTrades.map((t: any) => t.id)));
  };
  const selectedCount = selectedIds.size;
  const allSelected =
    selectedCount === actionableTrades.length && actionableTrades.length > 0;

  const handleVerify = async () => {
    if (selectedCount === 0) return;
    const actionableIds = Array.from(selectedIds).filter((id) => {
      const trade = filteredTrades.find((t: any) => t.id === id);
      return trade && (trade.state === 'INITIATED' || trade.state === 'READY');
    });
    if (actionableIds.length === 0) return;
    try {
      await verifySubTradeRequests({
        variables: {
          input: {
            subTradeRequestIds: actionableIds,
            organizationId: activeOrganization?.id,
          },
        },
        refetchQueries: ['fetchSubTradeRequestsFull', 'fetchSubTradeRequests'],
      });
      showToast({
        severity: 'success',
        message: `Verified ${actionableIds.length} trade(s)`,
      });
      setSelectedIds(new Set());
      refetchAll();
    } catch {
      // Error toast handled by useTradingMutation
    }
  };

  const handleInitiateBatch = async () => {
    setShowConfirmBatch(false);
    try {
      const result = await generateBulkTrades({
        variables: { input: { organizationId: activeOrganization?.id } },
      });
      const btrId = result.data?.generateBulkTrades?.bulkTradeRun?.id;
      if (btrId) {
        showToast({
          severity: 'success',
          message: 'Trade batch initiated successfully',
        });
        setPendingBulkTradeRunId(btrId);
        setActiveFlowStep('executing');
        setCurrentTab('operations');
        triggerRefresh();
      }
    } catch {
      // Error toast handled by useTradingMutation
    }
  };

  const handleCancel = async () => {
    setShowConfirmCancel(false);
    if (selectedCount === 0) return;
    const actionableIds = Array.from(selectedIds).filter((id) => {
      const trade = filteredTrades.find((t: any) => t.id === id);
      return trade && (trade.state === 'INITIATED' || trade.state === 'READY');
    });
    if (actionableIds.length === 0) return;
    try {
      await cancelSubTradeRequestsById({
        variables: {
          input: {
            subTradeRequestIds: actionableIds,
            organizationId: activeOrganization?.id,
          },
        },
        refetchQueries: ['fetchSubTradeRequestsFull', 'fetchSubTradeRequests'],
      });
      showToast({
        severity: 'success',
        message: `Cancelled ${actionableIds.length} trade(s)`,
      });
      setSelectedIds(new Set());
      refetchAll();
    } catch {
      // Error toast handled by useTradingMutation
    }
  };

  const handleCancelSingle = async (tradeId: string) => {
    try {
      await cancelSubTradeRequestsById({
        variables: {
          input: {
            subTradeRequestIds: [tradeId],
            organizationId: activeOrganization?.id,
          },
        },
        refetchQueries: ['fetchSubTradeRequestsFull', 'fetchSubTradeRequests'],
      });
      showToast({ severity: 'success', message: 'Trade cancelled' });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(tradeId);
        return next;
      });
      refetchAll();
    } catch {
      // Error toast handled by useTradingMutation
    }
  };

  const handleVerifySingle = async (tradeId: string) => {
    try {
      await verifySubTradeRequests({
        variables: {
          input: {
            subTradeRequestIds: [tradeId],
            organizationId: activeOrganization?.id,
          },
        },
        refetchQueries: ['fetchSubTradeRequestsFull', 'fetchSubTradeRequests'],
      });
      showToast({ severity: 'success', message: 'Trade verified' });
      refetchAll();
    } catch {
      // Error toast handled by useTradingMutation
    }
  };

  const handleUpdateAmount = async (
    tradeId: string,
    newAmountCents: number,
    currentPriceCents?: number,
  ) => {
    try {
      const updatePayload: Record<string, any> = {
        subTradeRequestId: tradeId,
        moneyAllocatedCents: Math.abs(newAmountCents),
      };
      if (currentPriceCents && currentPriceCents > 0) {
        updatePayload.expectedQuantity =
          Math.abs(newAmountCents) / currentPriceCents;
        updatePayload.expectedTradePriceCents = currentPriceCents;
      }
      await updateSubTradeRequests({
        variables: {
          input: {
            subTradeRequests: [updatePayload],
          },
        },
        refetchQueries: ['fetchSubTradeRequestsFull', 'fetchSubTradeRequests'],
      });
      showToast({ severity: 'success', message: 'Trade amount updated' });
      refetchAll();
    } catch {
      // Error toast handled by useTradingMutation
    }
  };

  const inBatchCount = useMemo(
    () =>
      trades.filter(
        (t: any) =>
          t.bulkTradeRun && (t.state === 'READY' || t.state === 'INITIATED'),
      ).length,
    [trades],
  );
  const orgWideEligible = Math.max(
    0,
    staged + approved - inBatchCount - blockedCount,
  );

  const columns: ColumnDef<any>[] = useMemo(
    () => [
      { id: 'expand', header: '', enableSorting: false },
      {
        id: 'product',
        header: 'Product',
        accessorFn: (row: any) => row.financialProduct?.ticker || '',
      },
      {
        id: 'side',
        header: 'Side',
        accessorFn: (row: any) => (isBuyType(row.type) ? 'BUY' : 'SELL'),
      },
      {
        id: 'ccy',
        header: 'CCY',
        accessorFn: (row: any) => row.currency || '',
      },
      {
        id: 'qty',
        header: 'Qty',
        accessorFn: (row: any) =>
          calculateQuantity(
            row.moneyAllocatedCents,
            row.financialProduct?.currentPriceCents,
          ),
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorFn: (row: any) => Math.abs(row.moneyAllocatedCents || 0),
      },
      {
        id: 'client',
        header: 'Client',
        accessorFn: (row: any) =>
          row.subAccount?.account?.user?.firstName || '',
      },
      {
        id: 'settlement',
        header: 'Settlement',
        accessorFn: (row: any) => row.settlementDate || '',
      },
    ],
    [],
  );

  const { table } = useTradingTable({ data: filteredTrades, columns });

  return (
    <Card>
      <Box p={2}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={1.5}
        >
          <Typography variant='titleMedium' weight='bold'>
            Trade Pipeline
          </Typography>
          <Box display='flex' gap={0.75} alignItems='center'>
            <Button
              size='sm'
              variant='filled'
              label={initiating ? 'Initiating...' : 'Initiate Trade Batch'}
              disabled={orgWideEligible === 0 || initiating}
              onClick={() => setShowConfirmBatch(true)}
            />
            <Chip text={`${pipelineTotal.toLocaleString()} total`} small />
            <Button
              size='sm'
              variant='outlined'
              label='Export CSV'
              onClick={() => exportTradesToCsv(filteredTrades)}
            />
          </Box>
        </Box>

        <ConfirmationModal
          open={showConfirmCancel}
          title='Cancel Trades'
          bodyText={`Cancel ${selectedCount} trade${selectedCount !== 1 ? 's' : ''}? This cannot be undone.`}
          confirmButtonLabel={cancelling ? 'Cancelling...' : 'Confirm Cancel'}
          confirmButtonColor={ConfirmButtonColors.destructive}
          loading={cancelling}
          onConfirm={handleCancel}
          onCancel={() => setShowConfirmCancel(false)}
        />

        <ConfirmationModal
          open={showConfirmBatch}
          title='Initiate Trade Batch'
          bodyText={[
            `This will batch all eligible trades across the organization (~${orgWideEligible} eligible).${inBatchCount > 0 ? ` ${inBatchCount} trade${inBatchCount !== 1 ? 's' : ''} already in an active batch will be excluded.` : ''} Cash validation will be applied.`,
          ].join('\n\n')}
          confirmButtonLabel={initiating ? 'Initiating...' : 'Confirm & Send'}
          confirmButtonVariant={ButtonVariants.filled}
          loading={initiating}
          onConfirm={handleInitiateBatch}
          onCancel={() => setShowConfirmBatch(false)}
        />

        {selectedCount > 0 && (
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={1.5}
            px={1.5}
            py={0.75}
            sx={{
              border: '1px solid',
              borderColor: sys.color.primary,
              borderRadius: '4px',
              bgcolor: sys.color.primaryVariant,
            }}
          >
            <Typography variant='labelSmall'>
              <strong>{selectedCount}</strong> trade
              {selectedCount !== 1 ? 's' : ''} selected
            </Typography>
            <Box display='flex' gap={0.75}>
              {activeOrganization?.requireSubTradeRequestApproval && (
                <Button
                  size='sm'
                  variant='filled'
                  label={verifying ? 'Verifying...' : 'Verify Selected'}
                  disabled={verifying}
                  onClick={handleVerify}
                />
              )}
              <Button
                size='sm'
                variant='outlined'
                label='Cancel Selected'
                disabled={cancelling}
                onClick={() => setShowConfirmCancel(true)}
              />
              <Button
                size='sm'
                variant='text'
                label='Clear'
                onClick={() => setSelectedIds(new Set())}
              />
            </Box>
          </Box>
        )}

        <Box
          display='flex'
          gap={0.5}
          mb={2}
          sx={{
            borderBottom: '1px solid',
            borderColor: sys.color.outlineVariant,
            pb: 1.5,
          }}
        >
          <StatPill
            label='Pending'
            value={pendingDisplay}
            color={sys.color.warning ?? ''}
            active={stateFilter === 'INITIATED'}
            onClick={() => {
              setStateFilter(stateFilter === 'INITIATED' ? 'ALL' : 'INITIATED');
              setPage(1);
            }}
            sys={sys}
          />
          <StatPill
            label='Ready'
            value={readyDisplay}
            color={sys.color.positive ?? ''}
            active={stateFilter === 'READY'}
            onClick={() => {
              setStateFilter(stateFilter === 'READY' ? 'ALL' : 'READY');
              setPage(1);
            }}
            sys={sys}
          />
          <StatPill
            label='Sent'
            value={sent}
            color={sys.color.primary ?? ''}
            active={stateFilter === 'REQUESTED'}
            onClick={() => {
              setStateFilter(stateFilter === 'REQUESTED' ? 'ALL' : 'REQUESTED');
              setPage(1);
            }}
            sys={sys}
          />
          {blockedCount > 0 && (
            <>
              <Box sx={{ ...BORDER_LEFT, mx: 0.5 }} />
              <StatPill
                label='Blocked'
                value={blockedCount}
                color={sys.color.negative ?? ''}
                active={stateFilter === 'BLOCKED'}
                onClick={() => {
                  setStateFilter(stateFilter === 'BLOCKED' ? 'ALL' : 'BLOCKED');
                  setPage(1);
                }}
                sys={sys}
              />
            </>
          )}
          <Box sx={{ ...BORDER_LEFT, mx: 0.5 }} />
          <StatPill
            label='Pipeline Value'
            value={formatMoneyValue(activeValue, '$', 2)}
            color={sys.color.primary ?? ''}
            sys={sys}
          />
        </Box>

        <Box
          display='flex'
          gap={1.5}
          mb={2}
          flexWrap='wrap'
          alignItems='center'
        >
          <SelectField
            label='Side'
            value={typeFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value='ALL'>All</MenuItem>
            <MenuItem value='BUY'>Buy</MenuItem>
            <MenuItem value='SELL'>Sell</MenuItem>
          </SelectField>
          {tradeCurrencyOptions.length > 1 && (
            <SelectField
              label='Currency'
              value={tradeCurrencyFilter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTradeCurrencyFilter(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 100 }}
            >
              <MenuItem value='ALL'>All</MenuItem>
              {tradeCurrencyOptions.map((ccy) => (
                <MenuItem key={ccy} value={ccy}>
                  {ccy}
                </MenuItem>
              ))}
            </SelectField>
          )}
          <TextField
            label='Search'
            placeholder='Ticker, client...'
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(e.target.value)
            }
            sx={{ minWidth: 220 }}
          />
        </Box>

        {loading ? (
          <Box>
            {[0, 1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                display='flex'
                gap={2}
                px={2}
                py={1}
                alignItems='center'
              >
                <Skeleton variant='rectangular' width={18} height={18} />
                <Skeleton variant='text' width='14%' />
                <Skeleton variant='text' width='8%' />
                <Skeleton variant='text' width='10%' />
                <Skeleton variant='text' width='12%' />
                <Skeleton variant='text' width='14%' />
                <Skeleton variant='text' width='8%' />
              </Box>
            ))}
          </Box>
        ) : filteredTrades.length === 0 ? (
          <EmptyState>
            <Typography variant='bodyMedium' sx={{ ...SECONDARY_TEXT_SX }}>
              No trades in pipeline. Create trades from the Portfolios tab.
            </Typography>
          </EmptyState>
        ) : (
          <>
            <Table sx={{ ...ALIGNED_TABLE_SX }}>
              <TableRow
                sx={{
                  '& th, & td': {
                    padding: densityConfig.cellPadding,
                    ...SECTION_HEADER_SX,
                  },
                }}
              >
                <TableHeadCell width='3%'>
                  <Checkbox
                    checked={allSelected}
                    onChange={() => selectAll()}
                    customStyle={{ p: 0 }}
                  />
                </TableHeadCell>
                <TableHeadCell width='3%' />
                {[
                  {
                    id: 'product',
                    width: '14%',
                    label: 'Product',
                    number: false,
                  },
                  { id: 'side', width: '5%', label: 'Side', number: true },
                  { id: 'ccy', width: '4%', label: 'CCY', number: false },
                  { id: 'qty', width: '7%', label: 'Qty', number: true },
                  { id: 'amount', width: '11%', label: 'Amount', number: true },
                  {
                    id: 'client',
                    width: '13%',
                    label: 'Client',
                    number: false,
                  },
                  {
                    id: 'settlement',
                    width: '7%',
                    label: 'Settlement',
                    number: false,
                  },
                ].map((col) => (
                  <TableHeadCell
                    key={col.id}
                    width={col.width}
                    onClick={table.getColumn(col.id)?.getToggleSortingHandler()}
                    sx={{ ...SORTABLE_HEADER_SX }}
                    number={col.number}
                  >
                    {col.label}
                    <SortIndicator
                      sorted={table.getColumn(col.id)?.getIsSorted() || false}
                    />
                  </TableHeadCell>
                ))}
                <TableHeadCell width='13%'>Actions</TableHeadCell>
              </TableRow>
              {table.getRowModel().rows.map((row) => {
                const isNew =
                  recentlyCreatedSTRIds.includes(row.original.id) ||
                  (lastBulkCreationTime &&
                    row.original.createdAt &&
                    new Date(row.original.createdAt).getTime() >=
                      lastBulkCreationTime - 5000 &&
                    Date.now() - lastBulkCreationTime < 300000);
                return (
                  <TradeRequestRow
                    key={row.original.id}
                    trade={row.original}
                    selected={selectedIds.has(row.original.id)}
                    onToggleSelect={() => toggleSelect(row.original.id)}
                    onCancel={handleCancelSingle}
                    onVerify={
                      activeOrganization?.requireSubTradeRequestApproval
                        ? handleVerifySingle
                        : undefined
                    }
                    onUpdateAmount={handleUpdateAmount}
                    isNew={!!isNew}
                  />
                );
              })}
              {filteredTrades.length > 0 && (
                <TableRow
                  sx={{
                    bgcolor: sys.color.neutralHover,
                    borderTop: '2px solid',
                    borderColor: sys.color.onSurface,
                  }}
                >
                  <TableCell sx={{ py: 0.75 }} />
                  <TableCell sx={{ py: 0.75 }} />
                  <TableCell sx={{ py: 0.75 }}>
                    <Typography
                      variant='bodySmall'
                      weight='bold'
                      sx={{ textTransform: 'uppercase' }}
                    >
                      Total ({filteredTrades.length})
                    </Typography>
                  </TableCell>
                  <TableCell number sx={{ py: 0.75 }}>
                    <Typography variant='bodySmall'>
                      <Typography
                        variant='labelSmall'
                        component='span'
                        sx={{
                          color: tradeSideColor(sys.color, true),
                          ...BOLD_SX,
                        }}
                      >
                        {
                          filteredTrades.filter((t: any) => isBuyType(t.type))
                            .length
                        }
                        B
                      </Typography>
                      {' / '}
                      <Typography
                        variant='labelSmall'
                        component='span'
                        sx={{
                          color: tradeSideColor(sys.color, false),
                          ...BOLD_SX,
                        }}
                      >
                        {
                          filteredTrades.filter((t: any) =>
                            isSellType(t.type, t.isSellAll),
                          ).length
                        }
                        S
                      </Typography>
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.75 }} />
                  <TableCell sx={{ py: 0.75 }} />
                  <TableCell number sx={{ py: 0.75 }}>
                    <Typography variant='bodySmall' weight='bold'>
                      {formatMoneyValue(
                        filteredTrades.reduce(
                          (sum: number, t: any) =>
                            sum + Math.abs(t.moneyAllocatedCents || 0),
                          0,
                        ),
                        '$',
                        2,
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={3} sx={{ py: 0.75 }} />
                </TableRow>
              )}
            </Table>

            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mt={2}
            >
              <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
                Showing {Math.min((page - 1) * pageSize + 1, paginationTotal)} –{' '}
                {Math.min(page * pageSize, paginationTotal)} of{' '}
                {paginationTotal.toLocaleString()}
              </Typography>
              <Box display='flex' gap={1} alignItems='center'>
                <Button
                  size='sm'
                  variant='outlined'
                  label='Previous'
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                />
                <Typography variant='bodySmall'>
                  Page {page} of{' '}
                  {Math.max(1, Math.ceil(paginationTotal / pageSize))}
                </Typography>
                <Button
                  size='sm'
                  variant='outlined'
                  label='Next'
                  onClick={() => setPage(page + 1)}
                  disabled={page >= Math.ceil(paginationTotal / pageSize)}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

const ReconciliationLogSection = () => {
  const { sys } = useThemeTokens();
  const { activeOrganization } = useContext(UserContext);
  const [expanded, setExpanded] = useState(false);

  const thirtyDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  }, []);

  const { loading, data } = useQuery(FETCH_RECENT_TRADE_TRANSACTIONS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id,
          types: ['BUY', 'SELL'],
          dateAfter: thirtyDaysAgo,
        },
        pagination: { perPage: 50, sortField: 'date', sortDesc: true },
      },
    },
    skip: !activeOrganization?.id || !expanded,
    fetchPolicy: 'cache-and-network',
  });

  const rawTransactions: TradeTransaction[] =
    data?.fetchTransactions?.transactions || [];
  const totalCount = data?.fetchTransactions?.totalCount || 0;

  const transactions = useMemo(
    () =>
      [...rawTransactions].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      ),
    [rawTransactions],
  );

  return (
    <Card>
      <Box
        p={2}
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          '&:hover': { bgcolor: sys.color.neutralHover },
        }}
      >
        <Box display='flex' alignItems='center' gap={1}>
          <Typography variant='titleMedium' weight='bold'>
            Reconciliation Log
          </Typography>
          <Typography variant='bodySmall' sx={{ ...SECONDARY_TEXT_SX }}>
            Settled buy/sell transactions (last 30 days)
          </Typography>
        </Box>
        <Box display='flex' alignItems='center' gap={1}>
          {totalCount > 0 && <Chip text={`${totalCount}`} small />}
          <ExpandMoreIcon
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              color: sys.color.onSurfaceVariant,
            }}
          />
        </Box>
      </Box>

      {expanded && (
        <Box px={2} pb={2}>
          {loading ? (
            <Box>
              {[0, 1, 2, 3].map((i) => (
                <Box key={i} display='flex' gap={2} px={2} py={1}>
                  <Skeleton variant='text' width='10%' />
                  <Skeleton variant='text' width='12%' />
                  <Skeleton variant='text' width='8%' />
                  <Skeleton variant='text' width='8%' />
                  <Skeleton variant='text' width='10%' />
                  <Skeleton variant='text' width='10%' />
                  <Skeleton variant='text' width='12%' />
                </Box>
              ))}
            </Box>
          ) : transactions.length === 0 ? (
            <EmptyState>
              <Typography variant='bodyMedium' sx={{ ...SECONDARY_TEXT_SX }}>
                No settled transactions in the last 30 days.
              </Typography>
            </EmptyState>
          ) : (
            <Table sx={{ ...ALIGNED_TABLE_SX }}>
              <TableRow
                sx={{ '& th, & td': { py: 0.5, ...SECTION_HEADER_SX } }}
              >
                <TableHeadCell>Date</TableHeadCell>
                <TableHeadCell>Product</TableHeadCell>
                <TableHeadCell>Side</TableHeadCell>
                <TableHeadCell number>Qty</TableHeadCell>
                <TableHeadCell number>Price</TableHeadCell>
                <TableHeadCell number>Value</TableHeadCell>
                <TableHeadCell>Ccy</TableHeadCell>
                <TableHeadCell>Client</TableHeadCell>
                <TableHeadCell>Status</TableHeadCell>
              </TableRow>
              {transactions.map((tx) => {
                const isBuy = tx.type === 'BUY' || tx.type === 'CANCEL_SELL';
                return (
                  <TableRow
                    key={tx.id}
                    sx={{
                      borderLeft: '3px solid',
                      borderLeftColor: tradeSideColor(sys.color, isBuy),
                      '&:hover': { bgcolor: sys.color.neutralHover },
                    }}
                  >
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant='bodySmall'>
                        {new Date(tx.date).toLocaleDateString()}
                      </Typography>
                      {tx.settleDate && (
                        <Typography
                          variant='labelSmall'
                          sx={{ ...SECONDARY_TEXT_SX }}
                        >
                          S: {new Date(tx.settleDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant='bodySmall' weight='bold'>
                        {tx.financialProduct?.ticker || '—'}
                      </Typography>
                      <Typography
                        variant='bodySmall'
                        sx={{
                          color: sys.color.onSurfaceVariant,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 120,
                        }}
                      >
                        {tx.financialProduct?.translatedName?.en || ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Badge
                        label={isBuy ? 'BUY' : 'SELL'}
                        color={isBuy ? 'positive' : 'negative'}
                      />
                    </TableCell>
                    <TableCell number sx={{ py: 0.5 }}>
                      <Typography variant='bodySmall'>
                        {tx.quantity.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell number sx={{ py: 0.5 }}>
                      <Typography variant='bodySmall'>
                        {formatMoneyValue(tx.priceCents, '$', 2)}
                      </Typography>
                    </TableCell>
                    <TableCell number sx={{ py: 0.5 }}>
                      <Typography variant='bodySmall' weight='bold'>
                        {formatMoneyValue(Math.abs(tx.valueCents), '$', 2)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant='bodySmall'>
                        {tx.currency || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant='bodySmall'>
                        {tx.subAccount?.account?.user?.firstName || '—'}
                        {tx.subAccount?.account?.user?.lastName
                          ? ` ${tx.subAccount.account.user.lastName}`
                          : ''}
                      </Typography>
                      <Typography
                        variant='bodySmall'
                        sx={{ ...SECONDARY_TEXT_SX }}
                      >
                        {tx.subAccount?.account?.type || ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {tx.pending ? (
                        <Badge label='PENDING' color='warning' />
                      ) : (
                        <Badge label='SETTLED' color='positive' />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table>
          )}
        </Box>
      )}
    </Card>
  );
};

const TradeRequestsTab = () => (
  <Box display='flex' flexDirection='column' gap={2}>
    <TradePipelineSection />
    <ReconciliationLogSection />
  </Box>
);

export default TradeRequestsTab;
